import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Shuffle, Zap, ArrowRight, Target, Flame, Sparkles, Medal, Gem, Crown, Trophy, RotateCcw, X } from "lucide-react";

const BADGE_ICONS = { sparkles: Sparkles, medal: Medal, gem: Gem, crown: Crown };

const VALID_CATS = ["all", "Federal Law", "SAFE Act", "Loan Products", "MLO Activities", "Ethics & Fraud"];

export default function FlashcardsPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = (() => {
    const c = searchParams.get("category");
    return c && VALID_CATS.includes(c) ? c : "all";
  })();
  const [card, setCard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [category, setCategory] = useState(initialCategory);
  const [stats, setStats] = useState({ total: 0, known: 0, needs_review: 0, unseen: 0 });
  const [session, setSession] = useState({ answered: 0, correct: 0, byCat: {} });
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [newBadge, setNewBadge] = useState(null);
  const sessionStart = useRef(Date.now());

  const categories = VALID_CATS;

  async function nextCard() {
    setLoading(true);
    setSelected(null);
    setRevealed(false);
    const { data } = await api.get(`/flashcards/quiz-card?category=${encodeURIComponent(category)}`);
    setCard(data);
    setLoading(false);
    const s = await api.get("/flashcards/stats");
    setStats(s.data);
  }

  async function loadStreak() {
    try {
      const { data } = await api.get("/flashcards/streak");
      setStreak(data);
    } catch (e) { /* silent */ }
  }

  useEffect(() => { nextCard(); }, [category]);
  useEffect(() => { loadStreak(); }, []);

  async function submit(idx) {
    if (revealed) return;
    setSelected(idx);
    setRevealed(true);
    const isCorrect = idx === card.correct_index;
    const cat = card.category;
    setSession((s) => {
      const byCat = { ...s.byCat };
      const c = byCat[cat] || { answered: 0, correct: 0 };
      byCat[cat] = { answered: c.answered + 1, correct: c.correct + (isCorrect ? 1 : 0) };
      return { answered: s.answered + 1, correct: s.correct + (isCorrect ? 1 : 0), byCat };
    });
    await api.post("/flashcards/review", { card_number: card.card_number, known: isCorrect });
  }

  async function endSession() {
    if (session.answered === 0) return;
    const duration = Math.round((Date.now() - sessionStart.current) / 1000);
    // Compute weak categories client-side: cats with accuracy < 60% AND at least 2 answers
    const weak = Object.entries(session.byCat)
      .filter(([, v]) => v.answered >= 2 && v.correct / v.answered < 0.6)
      .sort((a, b) => a[1].correct / a[1].answered - b[1].correct / b[1].answered)
      .map(([k]) => k);
    try {
      const { data } = await api.post("/flashcards/session", {
        answered: session.answered,
        correct: session.correct,
        incorrect: session.answered - session.correct,
        duration_sec: duration,
        weak_categories: weak,
      });
      if (data.new_badge) setNewBadge(data.new_badge);
      if (data.current_streak !== undefined) {
        setStreak((s) => ({ ...(s || {}), current_streak: data.current_streak, longest_streak: data.longest_streak }));
      }
    } catch (e) { /* silent */ }
    setReportData({ duration, weak });
    setReportOpen(true);
  }

  function resetSession() {
    setSession({ answered: 0, correct: 0, byCat: {} });
    sessionStart.current = Date.now();
    setReportOpen(false);
    setReportData(null);
    setNewBadge(null);
    nextCard();
  }

  const sessionPct = session.answered ? Math.round(session.correct / session.answered * 100) : 0;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="flashcards-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline mb-2">Adaptive Flashcard Quiz · 678 cards</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-navy)] tracking-tight leading-tight">Answer, then reveal.</h1>
            <p className="text-sm text-neutral-600 mt-2">Missed cards return more often. Deck reshuffles after every answer.</p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <MiniStat n={stats.known} l="Known" tone="emerald" />
            <MiniStat n={stats.needs_review} l="Review" tone="amber" />
            <MiniStat n={session.answered ? sessionPct + "%" : "—"} l="Session" tone="neutral" />
          </div>
        </div>

        {/* Streak strip */}
        {streak && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-[var(--dw-navy)]/10 rounded-sm px-4 py-3 bg-white/50" data-testid="streak-strip">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center border ${streak.current_streak > 0 ? "border-orange-400 bg-orange-50" : "border-neutral-200 bg-neutral-50"}`}>
                <Flame className={`w-5 h-5 ${streak.current_streak > 0 ? "text-orange-500" : "text-neutral-400"}`} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-serif text-xl leading-none text-[var(--dw-navy)]" data-testid="streak-count">
                  {streak.current_streak}<span className="text-xs font-sans text-neutral-500 ml-1">day{streak.current_streak === 1 ? "" : "s"}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                  Longest {streak.longest_streak} · {streak.total_practice_days} total
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="badges-row">
              {streak.badges && streak.badges.length > 0 ? (
                streak.badges.map((b) => {
                  const Icon = BADGE_ICONS[b.icon] || Sparkles;
                  return (
                    <div key={b.tier} title={`${b.label} — ${b.threshold} days`} className={`w-9 h-9 rounded-sm flex items-center justify-center border ${badgeTone(b.tier)}`} data-testid={`badge-${b.tier}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-neutral-400">Practice 3 days in a row to earn your first badge</div>
              )}
            </div>
          </div>
        )}

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`fc-cat-${c}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                category === c
                  ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]"
                  : "border-[var(--dw-navy)]/15 text-neutral-600 hover:border-[var(--dw-navy)]/40"
              }`}
            >
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>

        {loading || !card ? (
          <div className="dw-card h-96 animate-pulse" />
        ) : (
          <div className="flip-scene" data-testid="flip-scene">
            <div className={`flip-card ${revealed ? "is-flipped" : ""}`} data-testid={revealed ? "card-flipped" : "card-front"}>
              {/* FRONT — Question + Choices */}
              <div className="flip-face front dw-card p-6 md:p-8">
                <div className="flex items-center justify-between text-xs text-neutral-500 font-mono-plex mb-4">
                  <span data-testid="card-meta">#{card.card_number} · {card.category}</span>
                  <span className="flex items-center gap-1"><Target className="w-3 h-3" /> Priority queue: {card.queue_stats.needs_review} to review</span>
                </div>

                <div className="font-serif text-2xl md:text-3xl text-[var(--dw-navy)] leading-snug mb-8" data-testid="card-question">
                  {card.front}
                </div>

                <div className="space-y-2.5">
                  {card.choices.map((choice, i) => {
                    const isSelected = selected === i;
                    return (
                      <button
                        key={i}
                        onClick={() => submit(i)}
                        disabled={revealed}
                        data-testid={`fc-choice-${i}`}
                        className={`w-full text-left flex items-start gap-3 p-4 border rounded-sm transition-all ${
                          isSelected ? "border-[var(--dw-navy)] bg-[var(--dw-navy)]/[0.03]"
                          : "border-[var(--dw-navy)]/12 hover:border-[var(--dw-navy)]/40 bg-white"
                        } ${revealed ? "cursor-default" : "cursor-pointer"}`}
                      >
                        <div className={`w-7 h-7 shrink-0 flex items-center justify-center border rounded-sm font-mono-plex text-xs ${
                          isSelected ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-white"
                          : "border-[var(--dw-navy)]/25 text-[var(--dw-navy)]"
                        }`}>{"ABCD"[i]}</div>
                        <div className="text-sm leading-relaxed pt-0.5 whitespace-pre-wrap flex-1">{choice}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 text-xs text-neutral-500 font-mono-plex text-center tracking-wider">
                  Choose an answer — the card will flip to reveal.
                </div>
              </div>

              {/* BACK — Reveal */}
              <div
                className={`flip-face back dw-card p-6 md:p-8 flex flex-col ${
                  selected === card.correct_index
                    ? "bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40"
                    : "bg-gradient-to-br from-red-50 via-white to-red-50/40"
                }`}
                style={{ minHeight: "100%" }}
                data-testid="card-back"
              >
                <div className="flex items-center justify-between text-xs font-mono-plex mb-3">
                  <span className="text-neutral-500">#{card.card_number} · {card.category}</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${
                    selected === card.correct_index
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-red-500 bg-red-500 text-white"
                  }`} data-testid="reveal-verdict">
                    {selected === card.correct_index ? <><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Correct</> : <><XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Incorrect</>}
                  </span>
                </div>

                <div className="overline text-[10px] mb-1.5">Answer</div>
                <div className="font-serif text-xl md:text-2xl text-[var(--dw-navy)] leading-snug mb-5" data-testid="reveal-answer">
                  {card.choices[card.correct_index]}
                </div>

                {selected !== null && selected !== card.correct_index && (
                  <div className="mb-4 border-l-2 border-red-300 pl-3">
                    <div className="overline text-[10px] text-red-700 mb-1">Your answer</div>
                    <div className="text-sm text-red-900 line-through opacity-70" data-testid="your-answer">{card.choices[selected]}</div>
                  </div>
                )}

                <div className="mt-auto pt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--dw-navy)]/10">
                  <div className="text-xs text-neutral-500">
                    {selected === card.correct_index ? "Moved to Known." : "Stays in your review queue."}
                  </div>
                  <button
                    onClick={nextCard}
                    data-testid="next-card"
                    className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-2"
                  >
                    <Shuffle className="w-4 h-4" strokeWidth={1.5} /> Next card <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-500">
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
            Adaptive queue: 70% missed cards · 25% unseen · 5% known.
          </div>
          <button
            onClick={endSession}
            disabled={session.answered === 0}
            data-testid="end-session-btn"
            className="border border-[var(--dw-navy)]/20 rounded-sm px-3 py-1.5 text-xs text-[var(--dw-navy)] hover:border-[var(--dw-navy)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <Trophy className="w-3.5 h-3.5" strokeWidth={1.5} /> End session &amp; see report
          </button>
        </div>
      </div>

      {reportOpen && reportData && (
        <SessionReport
          session={session}
          duration={reportData.duration}
          weak={reportData.weak}
          newBadge={newBadge}
          streak={streak}
          onClose={() => setReportOpen(false)}
          onNewSession={resetSession}
        />
      )}
    </AppLayout>
  );
}

function MiniStat({ n, l, tone }) {
  const cls = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-900", amber: "bg-amber-50 border-amber-200 text-amber-900", neutral: "bg-neutral-50 border-neutral-200 text-neutral-800" }[tone];
  return (
    <div className={`border rounded-sm px-3 py-1.5 min-w-[70px] ${cls}`}>
      <div className="font-serif text-xl leading-none">{n}</div>
      <div className="text-[9px] uppercase tracking-widest mt-0.5 opacity-80">{l}</div>
    </div>
  );
}

function badgeTone(tier) {
  return {
    starter: "border-neutral-300 bg-neutral-50 text-neutral-600",
    bronze: "border-amber-400/70 bg-amber-50 text-amber-700",
    silver: "border-slate-400/70 bg-slate-50 text-slate-600",
    gold: "border-[var(--dw-gold)]/70 bg-[var(--dw-gold)]/8 text-[var(--dw-gold-soft)]",
  }[tier] || "border-neutral-300 bg-neutral-50";
}

function SessionReport({ session, duration, weak, newBadge, streak, onClose, onNewSession }) {
  const pct = session.answered ? Math.round(session.correct / session.answered * 100) : 0;
  const mastered = Object.entries(session.byCat).filter(([, v]) => v.correct / v.answered >= 0.8 && v.answered >= 2).map(([k]) => k);
  const suggested = weak.length > 0 ? weak[0] : "All categories";
  const durationMin = Math.floor(duration / 60);
  const durationSec = duration % 60;
  const NewBadgeIcon = newBadge ? (BADGE_ICONS[newBadge.icon] || Sparkles) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="session-report">
      <div className="max-w-lg w-full bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/20 rounded-sm shadow-2xl dw-fade-up">
        <div className="flex items-center justify-between border-b border-[var(--dw-navy)]/10 px-6 py-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
            <div className="font-serif text-xl text-[var(--dw-navy)]">Session complete</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-[var(--dw-navy)]" data-testid="close-report">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {newBadge && (
            <div className="border border-[var(--dw-gold)]/50 bg-[var(--dw-gold)]/10 rounded-sm p-4 flex items-center gap-3 dw-fade-up" data-testid="new-badge">
              <div className="w-11 h-11 rounded-sm border border-[var(--dw-gold)]/70 bg-[var(--dw-gold)]/15 flex items-center justify-center">
                {NewBadgeIcon && <NewBadgeIcon className="w-5 h-5 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />}
              </div>
              <div>
                <div className="overline text-[var(--dw-gold-soft)] text-[10px]">New badge unlocked</div>
                <div className="font-serif text-lg text-[var(--dw-navy)] leading-tight">{newBadge.label}</div>
                <div className="text-xs text-neutral-600 mt-0.5">{newBadge.streak}-day streak</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 text-center">
            <ReportStat label="Accuracy" value={`${pct}%`} tone={pct >= 80 ? "emerald" : pct >= 60 ? "amber" : "red"} testId="report-accuracy" />
            <ReportStat label="Answered" value={session.answered} tone="neutral" testId="report-answered" />
            <ReportStat label="Time" value={durationMin ? `${durationMin}m ${durationSec}s` : `${durationSec}s`} tone="neutral" testId="report-time" />
          </div>

          {/* Per-category breakdown */}
          {Object.keys(session.byCat).length > 0 && (
            <div>
              <div className="overline mb-2 text-[10px]">By Category</div>
              <div className="space-y-1.5">
                {Object.entries(session.byCat).sort((a, b) => b[1].answered - a[1].answered).map(([cat, v]) => {
                  const p = Math.round(v.correct / v.answered * 100);
                  return (
                    <div key={cat} className="flex items-center gap-3 text-xs" data-testid={`report-cat-${cat}`}>
                      <div className="w-32 truncate text-neutral-700">{cat}</div>
                      <div className="flex-1 h-1.5 bg-neutral-200 rounded-sm overflow-hidden">
                        <div className={`h-full ${p >= 80 ? "bg-emerald-500" : p >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${p}%` }} />
                      </div>
                      <div className="font-mono-plex text-neutral-600 tabular-nums w-16 text-right">{v.correct}/{v.answered} · {p}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mastered */}
          {mastered.length > 0 && (
            <div>
              <div className="overline mb-2 text-[10px] text-emerald-700">Mastered this session</div>
              <div className="flex flex-wrap gap-1.5">
                {mastered.map((c) => (
                  <span key={c} className="text-xs px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-sm">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Weak / suggested next focus */}
          <div className="border border-[var(--dw-navy)]/10 rounded-sm p-3 bg-white/60">
            <div className="overline mb-1 text-[10px]">Suggested next focus</div>
            {weak.length > 0 ? (
              <>
                <div className="text-sm text-[var(--dw-navy)] font-medium" data-testid="suggested-focus">Drill <span className="italic">{suggested}</span></div>
                <div className="text-xs text-neutral-600 mt-1">You scored below 60% in {weak.length === 1 ? "this area" : `${weak.length} areas`}. Filter by that category and run another session to lock it in.</div>
              </>
            ) : (
              <div className="text-sm text-[var(--dw-navy)]">Strong across the board. Try a fresh mix — the deck reshuffles automatically.</div>
            )}
          </div>

          {streak && streak.current_streak > 0 && !newBadge && (
            <div className="text-xs text-neutral-600 flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-orange-500" strokeWidth={1.5} />
              You&apos;re on a {streak.current_streak}-day streak. Come back tomorrow to keep it alive.
            </div>
          )}
        </div>

        <div className="border-t border-[var(--dw-navy)]/10 px-6 py-4 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="text-sm text-neutral-700 hover:text-[var(--dw-navy)] px-4 py-2" data-testid="review-later">
            Review later
          </button>
          <button onClick={onNewSession} className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="start-new-session">
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> New session
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportStat({ label, value, tone, testId }) {
  const cls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
    neutral: "border-neutral-200 bg-neutral-50 text-neutral-800",
  }[tone];
  return (
    <div className={`border rounded-sm px-3 py-3 ${cls}`} data-testid={testId}>
      <div className="font-serif text-2xl leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-widest mt-1 opacity-80">{label}</div>
    </div>
  );
}
